<template>
  <div
    class="c-aed-editableInput"
    :class="{
      active: isEditing && canEdit,
      editable: canEdit
    }"
    :ref="_uid"
  >
    <input
      class="c-aed-editableInput__input o-input"
      :class="{
        'o-input--no-edit': !canEdit,
        'o-input--textAligned': textAligned,
        'o-input--is-invalid': error
      }"
      :type="type"
      :maxlength="maxLength"
      v-model="mutableValue"
      :tabIndex="focusable"
      :readonly="!canEdit"
      :placeholder="canEdit ? translationsCustom.addValue || '-' : ''"
      @keydown="validateInput"
      @input="input($event)"
      ref="input"
      v-on:focus="handleFocus"
      @blur="submitIfLeave"
    />
    <div
      class=" c-aed-editableInput__editable-icon o-button o-button--light-blue o-button--action o-button--square"
      v-if="!isEditing && canEdit"
    >
      <svg class="o-svg-icon" viewBox="0 0 64 64" type="image/svg+xml">
        <use xlink:href="spritemap.svg#sprite-edit" x="0" y="0"></use>
      </svg>
    </div>
    <button
      class="o-button o-button--action o-button--round"
      v-if="isEditing && canSubmit && canEdit"
      @click="save"
      :disabled="!isValid"
      @blur="submitIfLeave"
    >
      <svg
        class="o-svg-icon o-button__icon"
        viewBox="0 0 64 64"
        type="image/svg+xml"
      >
        <use xlink:href="spritemap.svg#sprite-check-thinline" x="0" y="0"></use>
      </svg>
    </button>
    <button
      class="o-button o-button--action o-button--round o-button--light"
      v-if="isEditing && canCancel && canEdit"
      @click="cancel"
      @blur="submitIfLeave"
    >
      <svg
        class="o-svg-icon o-button__icon"
        viewBox="0 0 64 64"
        type="image/svg+xml"
      >
        <use xlink:href="spritemap.svg#sprite-cross" x="0" y="0"></use>
      </svg>
    </button>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "c-aed-editableInput",
  data() {
    return {
      isEditing: false,
      mutableValue: ""
    };
  },
  props: {
    section: { required: true },
    name: { required: true },
    value: { required: true },
    haveToSubmit: { type: Boolean, default: false },
    canSubmit: { type: Boolean, default: true },
    editState: { type: Boolean, default: false },
    canCancel: { type: Boolean, default: true },
    canEdit: { type: Boolean, default: true },
    textAligned: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    type: { type: String, default: "text" },
    allowEmpty: { type: Boolean, default: false }
  },
  computed: {
    ...mapState("translations", {
      translationsCustom: "custom"
    }),
    focusable() {
      const tabindex = this.canEdit ? "0" : "-1";
      return tabindex;
    },
    error() {
      return this.required && this.mutableValue === "" ? true : false;
    },
    maxLength() {
      return this.type === "tel" ? 8 : "";
    },
    isValid() {
      let isValid = true;
      switch (this.type) {
        case "tel": {
          isValid =
            this.mutableValue.length === 8 || this.mutableValue.length === 0;
          break;
        }
        case "email": {
          let isEmail = /^(("")("".+?(?<!\\)""@)|(([0-9a-z]((\.(?!\.))|[-!#$%&'*+/=?^`{}|~\w])*)(?<=[0-9a-z])@))((\[)(\[(\d{1,3}\.){3}\d{1,3}\])|(([0-9a-z][-0-9a-z]*[0-9a-z]*\.)+[a-z0-9][-a-z0-9]{0,22}[a-z0-9]))$/.test(
            this.mutableValue
          );
          if (this.allowEmpty && this.mutableValue === "") {
            isEmail = true;
          }
          isValid = isEmail;
          break;
        }
        default: {
          isValid = true;
        }
      }
      return isValid;
    }
  },
  watch: {
    value: function(newVal) {
      this.mutableValue = newVal;
    },
    editState: function(newVal) {
      this.isEditing = newVal;
    },
    isEditing: function() {
      if (this.isEditing) {
        this.$emit("editing", event);
      }
    }
  },
  methods: {
    async save() {
      this.isEditing = false;
      if (this.mutableValue !== this.value) {
        let payload = {
          api: this.$api,
          id: this.$store.state.aed.all.id,
          section: this.section,
          method: "PATCH",
          data: {
            [this.name]: this.mutableValue
          }
        };
        try {
          await this.$store.dispatch("aed/setSection", payload);
        } catch (err) {
          this.mutableValue = this.value;
        }
      }
    },
    cancel() {
      this.isEditing = false;
      this.mutableValue = this.value;
      this.$emit("valueReset");
      document.removeEventListener("mousedown", this.onClickOutside);
    },
    validateInput(event) {
      if (event.target.type === "tel") {
        this.onlyNumbers(event);
      }
    },
    input(event) {
      this.$emit("input", event.target.value);
    },
    onClickOutside(event) {
      event.stopPropagation();

      if (this.$refs[this._uid] === event.target.parentElement) return;
      this.canSubmit && this.isValid && this.isEditing && this.save();
    },
    submitIfLeave(event) {
      if (
        this.$refs[this._uid] ===
        (event.relatedTarget && event.relatedTarget.parentElement)
      )
        return;
      if (this.isValid) {
        this.canSubmit && this.isEditing && this.save();
      } else {
        this.isEditing && this.cancel();
      }
    },
    handleFocus() {
      this.isEditing = true;
      document.addEventListener("mousedown", this.onClickOutside);
    }
  },
  created() {
    this.mutableValue = this.value;
    this.isEditing = this.editState;
  }
};
</script>
<style lang="scss">
.c-aed-editableInput {
  $root: &;
  $input-left-padding: 10px;
  display: flex;
  position: relative;
  width: 100%;

  &--textAligned {
    margin-left: -$input-left-padding;
    width: calc(100% + 10px); //TODO INTERPOLATION does not work
  }

  input#{$root}__input {
    height: toRem(30px);
    border-color: transparent;
    vertical-align: middle;
    flex: 1 1 100%;
    width: 100%;

    &::placeholder {
      font-style: italic;
    }
    + .o-button {
      margin-left: 7px;
    }

    &.o-input--is-invalid {
      border: 1px solid $s-cp-sup-red;
      color: $s-cp-sup-red;

      &:focus {
        outline: $s-cp-sup-red auto 1px;
      }
    }
  }

  &:not(.active) {
    #{$root}__input {
      text-overflow: ellipsis;
    }
  }

  &.editable:hover {
    #{$root}__input {
      border-color: $s-cp-neutral-600;
    }
    div#{$root}__editable-icon {
      display: block;
      position: absolute;
      top: 0;
      right: 0;
    }
  }

  &.active {
    input#{$root}__input {
      border-color: $s-cp-neutral-600;

      &:focus {
        outline: $s-cp-sup-focus auto 1px;
      }
    }
  }

  div#{$root}__editable-icon {
    display: none;
    pointer-events: none;
  }

  .o-button {
    flex: 0 0 auto;
    * {
      pointer-events: none;
    }
    + .o-button {
      margin-left: 5px;
    }
  }
}
</style>
